import { memo, useCallback, useContext, useMemo, useRef, FC } from 'react'
import styled from 'styled-components'
import { transparentize, cover } from 'polished'

import TouchDeviceContext from 'contexts/TouchDeviceContext'

import ChevronDownIcon from '../../../icons/ChevronDown'
import StreamableVideo from '../../../display/StreamableVideo'
import ResponsiveImage from '../../../display/ResponsiveImage'
import type { ResponsiveImageProperties } from '../../../display/ResponsiveImage/types'

import LinkOrA from '../LinkOrA'
import { Slide } from '../styled'

import EmblaSlider from './EmblaSlider'
import { getDefaultImageVersion } from './helpers'

interface ArrowProps {
  left?: boolean
  right?: boolean
  show?: boolean
  portrait?: boolean
}

export const Arrow = styled.button<ArrowProps>`
  position: absolute;
  height: ${({ portrait }) => (portrait ? '36px' : '30px')};
  width: ${({ portrait }) => (portrait ? '36px' : '30px')};
  padding: 0;

  top: 50%;
  transform: translateY(-50%);

  opacity: 0;
  transition: opacity 300ms ease-out;

  &:disabled {
    visibility: hidden;
  }

  ${({ left }) =>
    left &&
    `
    left: 8px;
    svg {
      margin-left: -2px;
    }
  `}

  ${({ right }) =>
    right &&
    `
    right: 8px;
    svg {
      margin-right: -2px;
    }
  `}

  ${({ show }) =>
    show &&
    `
    opacity: 1 !important;
  `}

  appearance: none;
  outline: 0;
  border: none;
  border-radius: 50%;
  background-color: ${({ theme }) => transparentize(0.3, theme.brand.forest)};

  svg {
    display: inline-block;
    vertical-align: middle;
    height: ${({ portrait }) => (portrait ? '13px' : '8px')};
    margin-top: -2px;
    color: white;
    transform: rotate(${({ left }) => (left ? '90deg' : '-90deg')});
  }
`

export const Wrapper = styled.div<{ asLink?: boolean }>`
  ${cover()}
  &:hover {
    ${Arrow} {
      opacity: 1 !important;
    }
  }

  ${({ asLink }) =>
    asLink &&
    `
    cursor: pointer;
  `}
`

export const SliderWrapper = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;

  z-index: 0;

  > div {
    height: 100%;
  }
`

type StreamableVideoType = Pick<GQL_Video, 'id' | 'streamUrl' | 'thumbnailUrl'>
type CardSliderPhoto = {
  image: ResponsiveImageProperties
}

type CardSliderProps = {
  title: string
  photos?: CardSliderPhoto[]
  videos?: StreamableVideoType[]
  url?: string
  asLink?: boolean
  showArrows?: boolean
  portrait?: boolean
  eager?: boolean
  /**
   * Target length of the photos region (prefix images + regular photos),
   * excluding videos. When set, the photos region is padded with skeleton
   * slots up to this count and slides are keyed by position, so the slide
   * count is stable from first render and embla never re-inits mid-gesture.
   */
  reservedPhotoSlots?: number
}

const CardSlider = memo(
  ({
    title,
    photos = [],
    videos = [],
    url,
    asLink,
    showArrows,
    portrait,
    eager,
    reservedPhotoSlots,
  }: CardSliderProps) => {
    const wrapperRef = useRef(null)
    const scrollPrevRef = useRef<() => void>(() => {})
    const scrollNextRef = useRef<() => void>(() => {})

    const isTouchDevice = useContext(TouchDeviceContext)

    const onScrollPrev = useCallback((cb: () => void) => {
      scrollPrevRef.current = cb
    }, [])

    const onScrollNext = useCallback((cb: () => void) => {
      scrollNextRef.current = cb
    }, [])

    type SlideData = {
      isActive?: boolean
    }

    const keyedSlides: Array<{
      renderSlide: FC<SlideData>
      key: string
    }> = useMemo(() => {
      const videoSlides = videos.map(
        ({ id, streamUrl, thumbnailUrl: originalThumbnailUrl }, index) => {
          // when a video is the first slide, its poster should be the story cover photo
          const thumbnailUrl =
            index === 0
              ? getDefaultImageVersion(photos[0])
              : originalThumbnailUrl

          return {
            renderSlide: ({ isActive }: SlideData) => (
              <StreamableVideo
                src={streamUrl}
                poster={thumbnailUrl}
                forcePause={!isActive}
              />
            ),
            key: `video-${id}`,
          }
        },
      )

      const photoSlides = photos.map((photo, index) => {
        const alt = `Photo ${index + 1} for ${title}`
        return {
          renderSlide: () => (
            <Slide>
              <LinkOrA asLink={asLink} to={url}>
                <ResponsiveImage
                  {...photo.image}
                  zIndex={1}
                  alt={alt}
                  cover
                  eager={eager && index === 0}
                />
              </LinkOrA>
            </Slide>
          ),
          key: `photo-${getDefaultImageVersion(photo)}`,
        }
      })

      const skeletonCount =
        reservedPhotoSlots != null
          ? Math.max(0, reservedPhotoSlots - photos.length)
          : 0
      const skeletonSlides = Array.from({ length: skeletonCount }, (_, i) => ({
        renderSlide: () => <Slide aria-hidden data-testid="skeleton-slide" />,
        key: `skeleton-${i}`,
      }))

      const slides = [...videoSlides, ...photoSlides, ...skeletonSlides]

      // Position keys keep each slide node mounted as skeletons fill with real
      // photos (content swaps in place rather than a node being added/removed),
      // so embla's watchSlides never sees a childList mutation.
      if (reservedPhotoSlots != null) {
        return slides.map((slide, index) => ({
          ...slide,
          key: `slide-${index}`,
        }))
      }

      return slides
    }, [photos, videos, eager, reservedPhotoSlots, title, asLink, url])

    if (photos?.length === 0) {
      return null
    }

    const arrows = !isTouchDevice ? (
      <>
        <Arrow
          aria-label="Previous"
          onClick={() => scrollPrevRef.current()}
          show={showArrows}
          left
          portrait={portrait}
        >
          <ChevronDownIcon />
        </Arrow>
        <Arrow
          aria-label="Next"
          onClick={() => scrollNextRef.current()}
          show={showArrows}
          right
          portrait={portrait}
        >
          <ChevronDownIcon />
        </Arrow>
      </>
    ) : null

    return (
      <Wrapper ref={wrapperRef}>
        <SliderWrapper>
          <EmblaSlider
            title={title}
            keyedSlides={keyedSlides}
            arrows={arrows}
            portrait={portrait}
            onScrollPrev={onScrollPrev}
            onScrollNext={onScrollNext}
          />
        </SliderWrapper>
      </Wrapper>
    )
  },
  (prevProps, props) => {
    const samePhotoCount = prevProps?.photos?.length === props?.photos?.length
    const sameVideoCount = prevProps?.videos?.length === props?.videos?.length
    const sameUrl = prevProps?.url === props?.url
    const sameShowArrows = prevProps?.showArrows === props?.showArrows
    const sameEager = prevProps?.eager === props?.eager
    const sameReserved =
      prevProps?.reservedPhotoSlots === props?.reservedPhotoSlots
    const sameTitle = prevProps?.title === props?.title
    const sameAsLink = prevProps?.asLink === props?.asLink

    return (
      samePhotoCount &&
      sameVideoCount &&
      sameUrl &&
      sameShowArrows &&
      sameEager &&
      sameReserved &&
      sameTitle &&
      sameAsLink
    )
  },
)

export default CardSlider
